<template>
  <div class="finding-composer finding-composer--with-tags">
    <div
      class="finding-composer__field-wrapper"
      data-testid="findingComposer-container-fieldWrapper"
    >
      <v-text-field
        class="finding-composer__field"
        data-testid="routineExecution-field-findingComposer"
        dense
        hide-details
        outlined
        v-model="situation"
        :label="$t('assignments.finding_name')"
        :maxlength="situationMaxLength"
        @keyup.enter.prevent="createFinding()"
      />

      <div
        class="finding-composer__controls"
        data-testid="findingComposer-container-controls"
      >
        <div
          class="finding-composer__controls-left"
          data-testid="findingComposer-container-controlsLeft"
        >
          <TagsSearchPopover
            data-testid="findingComposer-popover-tagsSelector"
            :open="isTagsMenuOpen"
            :selectedTagIds="normalizedSelectedTagIds"
            :tags="menuTags"
            @openChange="isTagsMenuOpen = $event"
            @toggle-tag="handleToggleTagById"
          >
            <v-chip
              class="finding-composer__selector-chip cursor-pointer"
              data-testid="findingComposer-chip-selector"
              small
              :color="hasSelectedTags ? 'primary' : undefined"
              :dark="hasSelectedTags"
              :outlined="!hasSelectedTags"
            >
              {{ selectorChipText }}
            </v-chip>
          </TagsSearchPopover>
        </div>

        <div data-testid="findingComposer-container-controlsRight">
          <div
            class="finding-composer__actions"
            data-testid="findingComposer-container-actions"
          >
            <Button
              plain
              color="primary"
              size="small"
              data-testid="routineExecution-button-findingAddDetails"
              :text="$t('routines.add_more_details')"
              @click="showCreateFindingModal = true"
            />
            <v-icon
              data-testid="routineExecution-button-findingCreate"
              :color="!situation.trim() ? 'blue-grey' : 'primary'"
              @click="createFinding()"
            >
              mdi-send
            </v-icon>
          </div>
        </div>
      </div>
    </div>

    <p
      v-if="isFindingTagsRequired && !hasSelectedTags"
      class="blue-grey--text text--darken-1 text-caption mt-1 mb-0"
      data-testid="findingComposer-text-tagsRequired"
    >
      *{{ $t('routines.finding_tags_required') }}
    </p>

    <AddFindingModal
      v-if="showCreateFindingModal"
      :routineDefinition="routineDefinition"
      :selectedTagIds="normalizedSelectedTagIds"
      :situation="situation"
      :routine="routine"
      :show="showCreateFindingModal"
      :loading="isLoading"
      @close="showCreateFindingModal = false"
      @created="handleFindingCreatedFromModal"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { utils } from 'frontend-js-shared'
import { mapGetters } from 'vuex'

import api from '@/api'
import TagsSearchPopover from '@/components/TagsSearchPopover/TagsSearchPopover.vue'
import AddFindingModal from '@/containers/RoutineExecutionView/AddFindingModal.vue'
import Button from '@/components/UI/Button'
import mixin from '@/mixins/mixin'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

export default {
  name: 'FindingComposer',

  mixins: [mixin, trackerMixin],

  components: {
    AddFindingModal,
    Button,
    TagsSearchPopover,
  },

  props: {
    routineDefinition: {
      type: Object,
      default() {
        return {}
      },
    },
    routine: { type: Object, required: true },
    selectedTagIds: {
      type: Array,
      default() {
        return []
      },
    },
    tags: {
      type: Array,
      default() {
        return []
      },
    },
  },

  data() {
    return {
      isLoading: false,
      isTagsMenuOpen: false,
      situation: '',
      showCreateFindingModal: false,
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'organization', 'organizationTags']),

    defaultRevType() {
      if (this.organization?.featuresEnabled__quickAction) {
        return this.pascalCase(this.$constants.revTypes.quickAction)
      }

      if (this.organization?.featuresEnabled__improvementIdea) {
        return this.pascalCase(this.$constants.revTypes.improvementIdea)
      }

      return null
    },

    hasSelectedTags() {
      return !_.isEmpty(this.normalizedSelectedTagIds)
    },

    isFindingTagsRequired() {
      return Boolean(this.routineDefinition?.requireFindingTags)
    },

    menuTags() {
      const tagList = _.isEmpty(this.tags) ? this.organizationTags : this.tags
      if (_.isEmpty(tagList)) return tagList ?? []

      const allowedIds = this.routineDefinition?.availableTagsForFindings
      if (_.isEmpty(allowedIds)) return tagList

      const allowedSet = new Set(allowedIds.map(tag => utils.getEntityId(tag)))
      return tagList.filter(tag => allowedSet.has(utils.getEntityId(tag)))
    },

    normalizedSelectedTagIds() {
      return this.selectedTagIds.map(tagId => utils.getEntityId(tagId))
    },

    selectorChipText() {
      if (!this.hasSelectedTags) {
        return `+ ${this.$t('common.tag_other')}`
      }

      return this.$t('routines.selected_tagsWithCount', {
        count: this.normalizedSelectedTagIds.length,
      })
    },

    situationMaxLength() {
      return this.$constants.findings.situationMaxLength
    },
  },

  methods: {
    async createFinding(finding = {}) {
      const situation = finding?.situation ?? this.situation
      if (!situation?.trim?.()) return

      const findingTags = finding?.tags ?? this.normalizedSelectedTagIds
      if (this.isFindingTagsRequired && _.isEmpty(findingTags)) {
        this.showError(this.$t('routines.finding_tags_required'))
        return
      }

      try {
        this.isLoading = true
        await api.findings.create({
          organizationId: this.organizationId,
          data: {
            ...finding,
            media: finding?.media?.map?.(media => media.id) ?? [],
            assignees: finding?.assignees ?? [],
            situation,
            routine: this.routine.id,
            site: utils.getEntityId(this.routine.site),
            revType: finding?.revType ?? this.defaultRevType,
            tags: findingTags,
          },
        })
        this.trackCreateFinding({
          situation,
          hasMedia: Boolean(finding?.media?.length),
          hasAssignees: Boolean(finding?.assignees?.length),
          numberOfAssignees: finding?.assignees?.length ?? 0,
          revType: finding?.revType ?? this.$constants.revTypes.quickAction,
          hasDueDate: Boolean(finding?.dueDate),
        })
        this.situation = ''
        this.showCreateFindingModal = false
        this.$emit('created')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleFindingCreatedFromModal() {
      this.$emit('created')
      this.showCreateFindingModal = false
      this.situation = ''
    },

    handleToggleTagById(tagId) {
      if (!tagId) return

      this.$emit('toggle-tag', tagId)
    },
  },
}
</script>

<style lang="scss" scoped>
.finding-composer {
  position: relative;

  &__field-wrapper {
    position: relative;
  }

  &__controls {
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: 12px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__controls-left {
    display: flex;
    align-items: center;
  }

  &__selector-chip {
    :deep(.v-chip.v-chip--outlined) {
      background-color: white;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &--with-tags {
    .finding-composer__field {
      :deep(.v-input__slot) {
        min-height: 108px !important;
      }

      :deep(.v-text-field__slot input) {
        margin-bottom: 24px;
      }
    }
  }
}
</style>
